import { InputGroup } from '@pvh-components/input-group';
import React, { FocusEvent, useRef } from 'react';
import ChainableNumberField from './atoms/ChainableCleanNumberField';
import styles from './dateOfBirth.module.css';
import clsx from 'clsx';
import { toKebabCase } from '../../utils/casing';
import { DateFields } from '../DatePicker';
import { DatePickerProps } from '../DatePicker/DatePicker';
import useInputGroupBlurEvent from './hooks/useInputGroupBlurEvent';

export interface InputDescriptors {
  day: string;
  month: string;
  year: string;
}

type DateOfBirthProps = DatePickerProps & {
  inputNames?: InputDescriptors;
  inputFieldSeparator: '-' | '/';
  placeholders?: InputDescriptors;
};

const EMPTY_FIELD_STATE: DateFields = {
  DD: '',
  MM: '',
  YYYY: '',
};

/**
 * This is a controlled date picker component. This contains no validation other
 * than restricting the inputs to only contain numbers and have a maxLength.
 *
 * The function 'parseDateField' is offered to do easy validation of the state
 * of this component. It will return an errors list, that can be used for fine-
 * grained error
 */
const NewDateOfBirth = ({
  value = EMPTY_FIELD_STATE,
  onChange,
  isError = [],
  onBlur,
  required = false,
  placeholders = { day: 'DD', month: 'MM', year: 'YYYY' },
  inputNames = { day: 'day', month: 'month', year: 'year' },
  inputFieldSeparator = '-',
  dataTestIdPrefix,
  errorMessageText,
  ...rest
}: DateOfBirthProps) => {
  const { handleBlur, handleFocus } = useInputGroupBlurEvent(onBlur);

  const dayField = useRef<HTMLInputElement | undefined>();
  const monthField = useRef<HTMLInputElement | undefined>();
  const yearField = useRef<HTMLInputElement | undefined>();

  const handleBlurDay = (event: FocusEvent<HTMLInputElement>) => {
    onChange({ ...value, DD: padField(value.DD) });
    handleBlur(event);
  };

  const handleBlurMonth = (event: FocusEvent<HTMLInputElement>) => {
    onChange({ ...value, MM: padField(value.MM) });
    handleBlur(event);
  };

  const handleRightBoundDay = () => {
    monthField.current?.focus();
    monthField.current?.select();
  };

  const handleRightBoundMonth = () => {
    yearField.current?.focus();
    yearField.current?.select();
  };

  const isDateOfBirthComplete = () => {
    return (
      dayField?.current?.value.length === dayField?.current?.maxLength &&
      monthField?.current?.value.length === monthField?.current?.maxLength &&
      yearField?.current?.value.length === yearField?.current?.maxLength
    );
  };

  const focusFirstIncompleteField = (): void => {
    if (dayField?.current?.value.length !== dayField?.current?.maxLength) {
      dayField?.current?.focus();
      dayField?.current?.select();
      return;
    }
    if (monthField?.current?.value.length !== monthField?.current?.maxLength) {
      monthField?.current?.focus();
      monthField?.current?.select();
      return;
    }
    if (yearField?.current?.value.length !== yearField?.current?.maxLength) {
      yearField?.current?.focus();
      yearField?.current?.select();
      return;
    }
  };

  /**
   *
   * @param nodeEvent HTMLInputElement
   * This function checks if clicked element is input so it can be edited.
   * If it is it focuses that element, otherwise day input is focused.
   */

  const handleCompleteInputClick = (nodeEvent: HTMLInputElement): void => {
    if (nodeEvent.nodeName.toLowerCase() === 'input') {
      nodeEvent.focus();
      nodeEvent.select();
      return;
    }
    dayField?.current?.focus();
    dayField?.current?.select();
  };
  const handleDateOfBirthClick = (event: React.MouseEvent<HTMLDivElement>) =>
    isDateOfBirthComplete()
      ? handleCompleteInputClick(event.target as HTMLInputElement)
      : focusFirstIncompleteField();

  return (
    <div onClick={handleDateOfBirthClick}>
      <InputGroup
        isError={isError.length > 0}
        className={clsx(
          styles.dateOfBirthField,
          isError.length > 0 && styles['dateOfBirthField--error']
        )}
        errorMessageText={errorMessageText}
        {...rest}
      >
        <ChainableNumberField
          onRightBound={handleRightBoundDay}
          label={placeholders.day}
          name={inputNames.day}
          required={required}
          maxLength={2}
          value={value.DD}
          placeholder={placeholders.day}
          onChange={(event) => onChange({ ...value, DD: event.target.value })}
          onBlur={handleBlurDay}
          onFocus={handleFocus}
          ref={dayField}
          className={styles.dateOfBirthDayInput}
          dataTestIdPrefix={toKebabCase(dataTestIdPrefix, 'day')}
        />
        <span>{inputFieldSeparator}</span>
        <ChainableNumberField
          onRightBound={handleRightBoundMonth}
          onLeftBound={() => dayField.current?.focus()}
          label={placeholders.month}
          name={inputNames.month}
          required={required}
          maxLength={2}
          value={value.MM}
          placeholder={placeholders.month}
          onChange={(event) => onChange({ ...value, MM: event.target.value })}
          onBlur={handleBlurMonth}
          onFocus={handleFocus}
          ref={monthField}
          className={styles.dateOfBirthMonthInput}
          dataTestIdPrefix={toKebabCase(dataTestIdPrefix, 'month')}
        />
        <span>{inputFieldSeparator}</span>
        <ChainableNumberField
          onLeftBound={() => monthField.current?.focus()}
          label={placeholders.year}
          name={inputNames.year}
          required={required}
          maxLength={4}
          value={value.YYYY}
          placeholder={placeholders.year}
          onChange={(event) => onChange({ ...value, YYYY: event.target.value })}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={yearField}
          className={styles.dateOfBirtYearInput}
          dataTestIdPrefix={toKebabCase(dataTestIdPrefix, 'year')}
        />
      </InputGroup>
    </div>
  );
};

/**
 * Adds leading zeroes to the day and month fields. This is consciously
 * separated from the validation. Because this should be applied in different
 * situations.
 */
const padField = (n: string) => (n === '' ? '' : n.padStart(2, '0'));

export default NewDateOfBirth;
